import * as React from 'react'

import { useResizeObserver } from 'usehooks-ts'

import { useUnsafeEffect } from 'modules/core/hooks/useEffect'
import { usePrevious } from 'modules/core/hooks/usePrevious'

import { useUnsafeCallback } from './useCallback'

/**
 * A hook to paginate a table, and automatically calculate the page size to fit the container.
 *
 * It uses some refs to calculate the height of the table, rows, etc,
 * and propose the best page size.
 */
export function useFitTablePaginationHeight<T>(rows: T[]) {
  const containerRef = React.useRef<HTMLElement>(null)
  const tableRef = React.useRef<HTMLDivElement>(null)
  const bodyRef = React.useRef<HTMLTableSectionElement>(null)
  const rowRef = React.useRef<HTMLTableRowElement>(null)

  const { height: containerHeight } = useResizeObserver({ ref: containerRef })
  const { height: tableHeight } = useResizeObserver({ ref: tableRef })
  const { height: bodyHeight } = useResizeObserver({ ref: bodyRef })
  const { height: rowHeight } = useResizeObserver({ ref: rowRef })

  const previousContainerHeight = usePrevious(containerHeight)

  const [pageSize, setPageSize] = React.useState<number>(10)
  const [currentPage, setCurrentPage] = React.useState<number>(1)

  const totalPages = Math.ceil(rows.length / pageSize)

  const currentPageRows = React.useMemo(() => {
    const start = (currentPage - 1) * pageSize
    const end = start + pageSize

    return rows.slice(start, end)
  }, [rows, currentPage, pageSize])

  // Calculates the maximum amount of rows that fit in the container
  useUnsafeEffect(() => {
    if (
      // Only recalculate if the container height changes
      previousContainerHeight === containerHeight ||
      !containerHeight ||
      !tableHeight ||
      !rowHeight ||
      !bodyHeight ||
      !rows.length
    ) {
      return
    }

    // The total height for rows is the container height minus the header and footer
    const currentNonBodyHeight = tableHeight - bodyHeight
    const maxBodyHeight = containerHeight - currentNonBodyHeight

    const maxRows = Math.floor(maxBodyHeight / rowHeight)
    // Set a minimum amount of rows, to avoid edge cases
    const newPageSize = Math.max(2, maxRows)

    const newTotalPages = Math.ceil(rows.length / newPageSize)

    setPageSize(newPageSize)

    // If the user is in the last page, they will see the table
    // reducing its size when increasing height.
    // So we move to the previous page to avoid this.
    if (currentPage > newTotalPages - 1) {
      setCurrentPage(Math.max(1, newTotalPages - 1))
    }
  }, [
    containerHeight,
    previousContainerHeight,
    tableHeight,
    bodyHeight,
    rowHeight,
    currentPage,
    rows,
  ])

  const handleSetCurrentPage = useUnsafeCallback(
    (newCurrentPage) => {
      setCurrentPage(Math.max(1, Math.min(totalPages, newCurrentPage)))
    },
    [totalPages]
  )

  return {
    /**
     * The amount of rows per page
     */
    pageSize,
    /**
     * The total amount of pages
     */
    totalPages,
    /**
     * The current page, starting at 1
     */
    currentPage,
    /**
     * Function to set the current page
     */
    setCurrentPage: handleSetCurrentPage,
    /**
     * The rows to be rendered in the table
     */
    currentPageRows,
    /**
     * Ref for the container of the element.
     * The table will try to fit it, so the container should have a fixed height.
     */
    containerRef,
    /**
     * Ref for the table element.
     * It should fit the full table height, including header and footer, if any.
     */
    tableRef,
    /**
     * Ref for the body element.
     * It's the container of the rows. Only rows should be inside it.
     */
    bodyRef,
    /**
     * Ref for a single row element.
     * It's used to measure the height of the rows. Can be set on the first row.
     */
    rowRef,
  }
}
